import { createPortal } from 'react-dom';
import { Sparkles, X } from 'lucide-react';

// Per-turn AI decision record (mirrors chats/{id}/ai_decisions docs written by
// the chatolam ai-runner). Every field is optional/nullable — the legacy
// pipeline and older turns won't carry the full trace.
export interface AiDecision {
  id?: string;
  aiDecisionId?: string;
  at?: number;
  messageId?: string | null;
  intent?: string | null;
  intentConfidence?: number | null;
  sentiment?: string | null;
  kbResults?: number | null;
  topScore?: number | null;
  topSource?: string | null;
  rerankSource?: string | null;
  searchType?: string | null;
  queryRewritten?: string | null;
  faithful?: boolean | null;
  eotFinalVerdict?: string | null;
  outcome?: 'delivered' | 'suggested' | 'escalated' | null;
  brainEscalate?: boolean | null;
  lowConfidenceHold?: boolean | null;
  model?: string | null;
  /** Brain wall-clock latency for the turn, in ms (how long the reply took). */
  latencyMs?: number | null;
  tools?: string[] | null;
  toolCalls?: number | null;
  /** Approved support flow(s) that steered this turn (F8). */
  flowsApplied?: string[] | null;
  usage?: { inputTokens?: number; outputTokens?: number; costUsd?: number } | null;
  feedback?: { verdict?: 'good' | 'bad'; reason?: string; by?: string; at?: number } | null;
}

export interface Anchor {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

// ── Plain-language translation (for support agents, not engineers) ──────────
const INTENT_LABEL: Record<string, string> = {
  general: 'General question',
  technical: 'Technical help',
  billing: 'Billing',
  porting: 'Number porting',
  activation: 'Activation',
  coverage: 'Coverage',
  account: 'Account',
  escalation: 'Wants a human',
  reply: 'Follow-up',
  pricing: 'Pricing',
};
const TOOL_LABEL: Record<string, string> = {
  check_usage: 'data usage',
  get_billing_info: 'billing',
  check_porting_status: 'porting status',
  get_customer_context: 'account details',
  search_knowledge_base: 'knowledge base',
  search_support_memory: 'past cases',
  check_network_status: 'network status',
};
const titleCase = (s: string) => s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();

function intentLabel(i?: string | null): string | null {
  if (!i) return null;
  return INTENT_LABEL[i] ?? titleCase(i);
}
function confidence(n?: number | null): { word: string; cls: string } | null {
  if (typeof n !== 'number') return null;
  if (n >= 0.75) return { word: 'High', cls: 'bg-emerald-100 text-emerald-700' };
  if (n >= 0.5) return { word: 'Medium', cls: 'bg-amber-100 text-amber-700' };
  return { word: 'Low', cls: 'bg-rose-100 text-rose-700' };
}
function sourceLabel(src?: string | null, score?: number | null): string | null {
  if (!src) return typeof score === 'number' && score < 0.4 ? 'General knowledge' : null;
  return titleCase(src.replace(/\.md$/, '').replace(/^faq-?/, ''));
}
function matchWord(score?: number | null): string | null {
  if (typeof score !== 'number') return null;
  return score >= 0.6 ? 'strong' : score >= 0.4 ? 'partial' : 'weak';
}
// "How long the reply took" in seconds, agent-readable. Sub-second still reads
// as seconds (the user asked for seconds); long turns roll up to Xm Ys.
function latencyLabel(ms?: number | null): string | null {
  if (typeof ms !== 'number' || !isFinite(ms) || ms < 0) return null;
  const s = ms / 1000;
  // One decimal under a minute; otherwise round to whole seconds and carry the
  // 60s rollover so we never render "1m 60s" or "60.0s" at the boundary.
  if (s < 59.95) return `${s.toFixed(1)}s`;
  const total = Math.round(s);
  return `${Math.floor(total / 60)}m ${total % 60}s`;
}
const OUTCOME: Record<string, { label: string; cls: string }> = {
  delivered: { label: 'Sent to customer', cls: 'bg-emerald-100 text-emerald-700' },
  suggested: { label: 'Held for your review', cls: 'bg-amber-100 text-amber-700' },
  escalated: { label: 'Escalated to a human', cls: 'bg-rose-100 text-rose-700' },
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  if (children == null || children === '') return null;
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-gray-50 last:border-0">
      <span className="text-[11px] text-gray-500 shrink-0">{label}</span>
      <span className="text-xs text-gray-800 text-right" dir="auto">{children}</span>
    </div>
  );
}

interface AiWhyPopoverProps {
  decision: AiDecision;
  onClose: () => void;
  anchor?: Anchor | null;
}

const PANEL_W = 264;

/**
 * On-demand, support-agent-friendly "why did the bot reply this way" card.
 * Fixed-positioned beside the message (computed from the trigger rect) so it
 * floats over the thread and is never clipped by the scroll container.
 */
export function AiWhyPopover({ decision: d, onClose, anchor }: AiWhyPopoverProps) {
  const conf = confidence(d.intentConfidence);
  const intent = intentLabel(d.intent);
  const src = sourceLabel(d.topSource, d.topScore);
  const match = d.topSource ? matchWord(d.topScore) : null;
  const mood = d.sentiment ? titleCase(d.sentiment) : null;
  const outcome = d.outcome ? OUTCOME[d.outcome] : null;
  const latency = latencyLabel(d.latencyMs);
  const lookedUp = (d.tools ?? []).map((t) => TOOL_LABEL[t] ?? titleCase(t)).filter(Boolean);
  const hasAny = !!(intent || mood || src || d.faithful != null || outcome || lookedUp.length || latency || d.flowsApplied?.length);

  // One plain-English sentence: the quickest "why this answer" for an agent.
  const summary = (() => {
    const parts: string[] = [];
    if (intent) parts.push(`Read as a ${intent.toLowerCase()}${conf ? ` (${conf.word.toLowerCase()} confidence)` : ''}`);
    if (src) parts.push(`answered from the ${src}`);
    else if (lookedUp.length) parts.push(`after checking ${lookedUp.join(', ')}`);
    else if (typeof d.topScore === 'number') parts.push('answered from general knowledge (no strong match)');
    if (outcome) parts.push(outcome.label.toLowerCase());
    return parts.length ? parts.join(', ') + '.' : null;
  })();

  // Fixed positioning, clamped to the viewport on all sides. Rendered through a
  // portal to <body> so no transformed/overflow ancestor can reposition or clip
  // it (the cause of the earlier "cut off"). maxHeight + scroll for safety.
  const m = 8;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  let left: number;
  let top: number;
  if (anchor) {
    left = anchor.left - PANEL_W - m; // prefer left of the bubble
    if (left < m) left = anchor.right + m; // else flip to the right
    left = Math.max(m, Math.min(left, vw - PANEL_W - m));
    top = Math.max(m, Math.min(anchor.top, vh - 160));
  } else {
    left = vw - PANEL_W - 16;
    top = 80;
  }
  const style: React.CSSProperties = {
    position: 'fixed',
    top,
    left,
    width: PANEL_W,
    maxHeight: vh - top - m,
    overflowY: 'auto',
    zIndex: 1000,
  };

  return createPortal(
    <>
      {/* click-away to dismiss */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={onClose} />
      <div
        className="rounded-xl border border-gray-200 bg-white shadow-2xl p-3 text-left"
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
          <Sparkles className="w-3.5 h-3.5 text-violet-500" /> Why this reply
        </span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" title="Close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {summary && (
        <p className="mb-2 text-[12px] leading-snug text-gray-700 bg-violet-50 rounded-md px-2 py-1.5" dir="auto">
          {summary}
        </p>
      )}

      {hasAny ? (
        <div>
          <Row label="Understood as">
            {intent && (
              <span className="inline-flex items-center gap-1.5">
                {intent}
                {conf && <span className={`rounded-full px-1.5 py-px text-[10px] font-medium ${conf.cls}`}>{conf.word}</span>}
              </span>
            )}
          </Row>
          <Row label="Customer mood">{mood}</Row>
          <Row label="Answer from">
            {src && (
              <span>
                {src}
                {match && <span className="text-gray-400"> · {match}</span>}
              </span>
            )}
          </Row>
          <Row label="Fact-check">
            {d.faithful === true ? (
              <span className="text-emerald-600">✓ matches our info</span>
            ) : d.faithful === false ? (
              <span className="text-rose-600">⚠ not fully verified</span>
            ) : null}
          </Row>
          <Row label="Looked up">{lookedUp.length ? lookedUp.join(', ') : null}</Row>
          <Row label="Followed flow">
            {d.flowsApplied && d.flowsApplied.length ? (
              <span className="text-indigo-600">{d.flowsApplied.join(', ')}</span>
            ) : null}
          </Row>
          <Row label="Outcome">
            {outcome && <span className={`rounded-full px-1.5 py-px text-[10px] font-medium ${outcome.cls}`}>{outcome.label}</span>}
          </Row>
          <Row label="Response time">
            {latency && <span className="tabular-nums">{latency}</span>}
          </Row>
          {d.lowConfidenceHold && (
            <Row label="Why held">held for review — bot was unsure</Row>
          )}
          {d.feedback?.verdict && (
            <Row label="Your rating">
              <span className={d.feedback.verdict === 'good' ? 'text-emerald-600' : 'text-rose-600'}>
                {d.feedback.verdict === 'good' ? '👍 good' : '👎 needs work'}
              </span>
            </Row>
          )}
        </div>
      ) : (
        <p className="text-[11px] text-gray-400">No explanation captured for this reply.</p>
      )}
      </div>
    </>,
    document.body
  );
}

export default AiWhyPopover;
